'use client';

import {
  Alert,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@commenda/kakashi';
import React, { useContext, useMemo } from 'react';
import { useContentLibraryFields } from '../../hooks/useContentLibraryFields';
import { FormContext } from '../../hooks/FormContext';
import { hasMissingContentLibraryInputValues } from '../../utils/context-requirements';
import type { DynamicFieldDefinition, DynamicFieldsProps } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

interface DynamicFieldsComponentProps {
  fieldId?: string;
  label?: string;
  description?: string;
  guidance?: { text?: string };
  props?: DynamicFieldsProps;
  dataSource?: {
    type: 'content-library';
    contentLibrary?: {
      contentId: string;
      inputs?: Array<{
        key: string;
        source: 'static' | 'context' | 'field';
        value?: unknown;
        contextPath?: string;
        fieldId?: string;
        expectedType?: string;
      }>;
    };
  };
}

/**
 * Gets a nested value from an object using dot notation path.
 */
function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Maps a source field definition to the standard DynamicFieldDefinition schema.
 */
function mapFieldDefinition(
  sourceField: Record<string, unknown>,
  fieldMapping?: DynamicFieldsProps['fieldMapping'],
  typeMapping?: DynamicFieldsProps['typeMapping']
): DynamicFieldDefinition {
  const mapping = {
    key: fieldMapping?.key || 'key',
    label: fieldMapping?.label || 'label',
    type: fieldMapping?.type || 'type',
    required: fieldMapping?.required || 'required',
    displayOrder: fieldMapping?.displayOrder || 'displayOrder',
    groupKey: fieldMapping?.groupKey || 'groupKey',
  };

  const rawType = String(getNestedValue(sourceField, mapping.type) || 'string');
  const mappedType = typeMapping?.[rawType] || rawType.toLowerCase();

  return {
    key: String(getNestedValue(sourceField, mapping.key) || ''),
    label: String(getNestedValue(sourceField, mapping.label) || ''),
    type: mappedType as DynamicFieldDefinition['type'],
    required: Boolean(getNestedValue(sourceField, mapping.required)),
    displayOrder: Number(
      getNestedValue(sourceField, mapping.displayOrder) || 0
    ),
    groupKey: getNestedValue(sourceField, mapping.groupKey) as
      | string
      | undefined,
    placeholder: sourceField.placeholder as string | undefined,
    options: sourceField.options as
      | { value: string; label: string }[]
      | undefined,
    regex: sourceField.regex as string | undefined,
    disabled: sourceField.disabled as boolean | undefined,
    hidden: sourceField.hidden as boolean | undefined,
  };
}

/**
 * DynamicFields Component
 *
 * Dynamically renders form fields based on a schema. The schema can come from:
 * - Static configuration (type: 'static')
 * - Another field's value/metadata (type: 'field')
 * - DSL context (type: 'context')
 * - Content Library API (type: 'content-library')
 *
 * Usage Examples:
 *
 * 1. Content Library source (recommended for credentials):
 *    fieldsSource: {
 *      type: 'content-library',
 *      contentId: 'indirect-tax-portal-fields',
 *      inputs: [
 *        { key: 'corporationId', source: 'context', contextPath: 'corporationId' },
 *        { key: 'jurisdictionId', source: 'field', fieldId: 'jurisdictionId' },
 *        { key: 'taxType', source: 'field', fieldId: 'taxType' },
 *      ]
 *    }
 *
 * 2. Static source:
 *    fieldsSource: {
 *      type: 'static',
 *      fields: [{ key: 'name', label: 'Name', type: 'string', required: true }]
 *    }
 */
export const DynamicFieldsComponent: React.FC<DynamicFieldsComponentProps> = ({
  fieldId,
  label,
  description,
  guidance,
  props,
  dataSource,
}) => {
  const { formData, updateField, errors, dslContext, componentMap } =
    useContext(FormContext);

  const {
    fieldsSource,
    fieldPrefix: configuredFieldPrefix,
    layout = 'vertical',
    fieldMapping,
    typeMapping,
  } = props || {};

  // Use fieldId as prefix if available, otherwise fall back to configured fieldPrefix or default
  const fieldPrefix = fieldId || configuredFieldPrefix || 'dynamicFields';

  // Support both fieldsSource (new pattern) and dataSource (PropertyEditor pattern)
  // dataSource is used by the generic PropertyEditor UI for Content Library config
  // Prefer dataSource.contentLibrary if it exists, as that's what PropertyEditor saves to
  const effectiveFieldsSource =
    dataSource?.type === 'content-library' &&
    dataSource.contentLibrary?.contentId
      ? {
          type: 'content-library' as const,
          contentId: dataSource.contentLibrary.contentId,
          inputs: dataSource.contentLibrary.inputs,
        }
      : fieldsSource;

  // Check if we're using Content Library source
  const isContentLibrarySource =
    effectiveFieldsSource?.type === 'content-library';
  const contentLibraryConfig = isContentLibrarySource
    ? {
        contentId: effectiveFieldsSource.contentId,
        inputs: effectiveFieldsSource.inputs,
      }
    : undefined;

  // Fetch fields from Content Library if using that source type
  const {
    fields: contentLibraryFields,
    isLoading: isLoadingContentLibrary,
    error: contentLibraryError,
  } = useContentLibraryFields({
    config: contentLibraryConfig,
    dslContext,
    formData,
    skip: !isContentLibrarySource,
  });

  // Resolve field definitions from non-Content-Library sources
  const localFieldDefinitions = useMemo(() => {
    if (!effectiveFieldsSource || isContentLibrarySource) {
      return [];
    }

    try {
      let rawFields: unknown[] = [];

      switch (effectiveFieldsSource.type) {
        case 'static': {
          rawFields = effectiveFieldsSource.fields || [];
          break;
        }

        case 'field': {
          const sourceValue = formData[effectiveFieldsSource.fieldId];

          if (!sourceValue) {
            return [];
          }

          // Direct field value access
          const pathValue = getNestedValue(
            sourceValue as Record<string, unknown>,
            effectiveFieldsSource.path
          );
          if (Array.isArray(pathValue)) {
            rawFields = pathValue;
          }
          break;
        }

        case 'context': {
          if (dslContext) {
            const pathValue = getNestedValue(
              dslContext,
              effectiveFieldsSource.path
            );
            if (Array.isArray(pathValue)) {
              rawFields = pathValue;
            }
          }
          break;
        }
      }

      // Map and filter fields
      let mappedFields = rawFields
        .filter(
          (f): f is Record<string, unknown> =>
            f !== null && typeof f === 'object'
        )
        .map((f) => mapFieldDefinition(f, fieldMapping, typeMapping))
        .filter((f) => f.key && f.label);

      // Filter out hidden fields
      mappedFields = mappedFields.filter((f) => !f.hidden);

      // Sort by displayOrder
      mappedFields.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );

      return mappedFields;
    } catch {
      return [];
    }
  }, [
    effectiveFieldsSource,
    formData,
    dslContext,
    fieldMapping,
    typeMapping,
    isContentLibrarySource,
  ]);

  // Use Content Library fields or local fields
  const fieldDefinitions = isContentLibrarySource
    ? contentLibraryFields
    : localFieldDefinitions;

  const isLoading = isContentLibrarySource && isLoadingContentLibrary;
  const error = isContentLibrarySource ? contentLibraryError : null;

  // Get field value from formData using prefix
  const getFieldValue = (key: string): unknown => {
    const fullPath = fieldPrefix ? `${fieldPrefix}.${key}` : key;
    return formData[fullPath];
  };

  // Handle field change
  const handleFieldChange = (key: string, value: unknown) => {
    const fullPath = fieldPrefix ? `${fieldPrefix}.${key}` : key;
    updateField(fullPath, value);
  };

  // Get error for a field
  const getFieldError = (key: string): string | undefined => {
    const fullPath = fieldPrefix ? `${fieldPrefix}.${key}` : key;
    return errors[fullPath];
  };

  // Render a single field based on its type
  const renderField = (field: DynamicFieldDefinition) => {
    const value = getFieldValue(field.key);
    const fieldError = getFieldError(field.key);
    const isRequired = field.required;

    const labelElement = (
      <Label className="txt-small-medium text-text-secondary">
        {field.label}
        {isRequired && (
          <span className="text-utility-violet-600 ml-0.5">*</span>
        )}
      </Label>
    );

    const errorElement = fieldError && (
      <span className="txt-small-regular text-red-500">{fieldError}</span>
    );

    const placeholder =
      field.placeholder || `Enter ${field.label.toLowerCase()}`;

    switch (field.type) {
      case 'secureString':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="password"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'number':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="number"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'date':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="date"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Textarea
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={fieldError ? 'border-red-500' : ''}
            />
            {errorElement}
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Select
              value={(value as string) || ''}
              onValueChange={(val) => handleFieldChange(field.key, val)}
              disabled={field.disabled}
            >
              <SelectTrigger
                className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errorElement}
          </div>
        );

      case 'email':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="email"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'phone':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="tel"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'url':
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="url"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );

      case 'string':
      default:
        return (
          <div key={field.key} className="flex flex-col gap-1.5">
            {labelElement}
            <Input
              type="text"
              value={(value as string) || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={placeholder}
              disabled={field.disabled}
              className={`h-11 ${fieldError ? 'border-red-500' : ''}`}
            />
            {errorElement}
          </div>
        );
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <LoadingIndicator />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        variant="warning"
        title="Failed to load fields"
        description={error.message}
      />
    );
  }

  // No source configured
  if (!effectiveFieldsSource) {
    return (
      <Alert
        variant="info"
        title="No field source configured"
        description="Configure a Data Source to render dynamic fields."
      />
    );
  }

  // No fields to render
  if (fieldDefinitions.length === 0) {
    // For content-library source, check if inputs are missing
    const effectiveInputs = isContentLibrarySource
      ? effectiveFieldsSource.inputs
      : undefined;
    if (isContentLibrarySource && effectiveInputs) {
      const hasMissingInputs = hasMissingContentLibraryInputValues(
        effectiveInputs,
        dslContext,
        formData
      );

      if (hasMissingInputs) {
        return (
          <div className="py-4 text-center text-text-tertiary txt-small-regular">
            Complete the previous fields to see available options
          </div>
        );
      }
    }

    return (
      <Alert
        variant="info"
        title="No fields available"
        description="No dynamic fields are defined for the current selection."
      />
    );
  }

  // Render fields
  const fieldsContent =
    layout === 'grid' && fieldDefinitions.length >= 2 ? (
      <div className="grid grid-cols-2 gap-4">
        {fieldDefinitions.map(renderField)}
      </div>
    ) : (
      <div className="flex flex-col gap-4">
        {fieldDefinitions.map(renderField)}
      </div>
    );

  return (
    <div className="flex flex-col gap-4">
      {label && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
        />
      )}
      {fieldsContent}
    </div>
  );
};
